import { Component, OnInit, ViewEncapsulation, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { feedAnimations } from "app/animations";
import { EvaluationService } from "app/services/evaluation/evaluation.service";
import { ProjectService } from "app/services/project/project.service";
import { UserService } from "app/services/user/user.service";
import moment from "app/util/dayjs";
import { firstValueFrom } from "rxjs";
import { SendPollComponent } from "../send-poll-dialog/send-poll-dialog.component";

@Component({
    selector: "app-form",
    templateUrl: "./form.component.html",
    styleUrls: ["./form.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: feedAnimations,
    standalone: false
})
export class FormComponent implements OnInit {
    params;
    readonly user = signal<any>(null);
    readonly editMode = signal(false);

    readonly periodSelected = signal<any>(null);
    readonly evaluationForm = signal<any>(null);
    readonly selectedBlock = signal<any>(null);
    readonly evaluation = signal<any>(null);

    readonly projectId = signal<number | undefined>(undefined);
    sectionsRequired = [];
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private sanitizer: DomSanitizer,
        private dialog: MatDialog,
        private userSvc: UserService,
        private projectSvc: ProjectService,
        private evaluationSvc: EvaluationService,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.projectSvc.getProjectConfig(false).subscribe((project) => {
            this.projectId.set(project.id);
            if (!project.enabled_mod_polls) {
                this.router.navigate(["/inicio"]);
            }
        });
        this.route.queryParams.subscribe(async (params) => {
            this.params = params;
            try {
                const userData = await firstValueFrom(this.userSvc.getUser());
                this.user.set(userData);

                const periodData = await firstValueFrom(this.evaluationSvc.getEvaluationPeriodById(this.params.id));
                const period = { ...periodData };
                if (period.headerImg) {
                    period.headerImg = period.headerImg;
                }
                switch (localStorage.getItem("currentLang")) {
                    case "it":
                        if (period.traducciones?.it) {
                            period.name = period.traducciones.it.name;
                            period.headerContent = period.traducciones.it.header_content;
                        }
                        break;
                    case "en":
                        if (period.traducciones?.en) {
                            period.name = period.traducciones.en.name;
                            period.headerContent = period.traducciones.en.header_content;
                        }
                        break;
                    case "fr":
                        if (period.traducciones?.fr) {
                            period.name = period.traducciones.fr.name;
                            period.headerContent = period.traducciones.fr.header_content;
                        }
                        break;
                    case "pt":
                        if (period.traducciones?.pt) {
                            period.name = period.traducciones.pt.name;
                            period.headerContent = period.traducciones.pt.header_content;
                        }
                        break;
                    default:
                        break;
                }
                this.periodSelected.set(period);

                const formData = await firstValueFrom(this.evaluationSvc.getEvaluationFormByIdPOLL(this.params.formId));
                this.evaluationForm.set(formData);
                this.selectedBlock.set(formData.blocks?.[0] ?? null);
                this.getRequiredId();
                await this.loadPoll();
            } catch (err) {
                console.error("Error loading poll form", err);
            }
        });
    }
    getRequiredId() {
        this.sectionsRequired = [];
        const bloques = this.evaluationForm()?.blocks ?? [];

        for (let bloque of bloques) {
            for (let section of bloque.sections) {
                for (let element of section.elements) {
                    if (element.required == 1) this.sectionsRequired.push(element.id);
                }
            }
        }
    }

    async loadPoll() {
        const user = this.user();
        const period = this.periodSelected();
        if (!user?.id || !period?.id) {
            return;
        }

        const data = await firstValueFrom(
            this.evaluationSvc.getEvaluation(this.params.formId, user.id, period.id, "0")
        );

        let evaluation: any;
        if (!data) {
            evaluation = {
                id: null,
                personId: user.id,
                evaluationFormId: this.params.formId,
                periodId: period.id,
                creationDate: null,
                updateDate: null,
                iterationNumber: 0,
                totalResult: 0,
                partial: null
            };
        } else {
            evaluation = { ...data };
        }

        const partialData = await firstValueFrom(this.evaluationSvc.getEvaluationPartial(user.id, user.id, period.id));

        if (!partialData) {
            evaluation.partial = {
                id: null,
                comment: null,
                creationDate: null,
                updateDate: null,
                evaluationId: null,
                evaluatorId: user.id,
                status: "DRAFT",
                results: []
            };
        } else {
            evaluation.partial = partialData;
        }

        const _isActive = moment().isBetween(period.startPeriod, period.endPeriod);
        this.editMode.set(evaluation.partial.status !== "DONE" && _isActive);
        this.evaluation.set(evaluation);
    }

    findElementResult(elementId) {
        const _emptyResult = { result: null, comment: null };
        const evaluation = this.evaluation();
        if (evaluation?.partial) {
            const elementResult = evaluation.partial.results.find(
                (r) => (r.elementId || r.actionId || r.sectionId || r.blockId) === elementId
            );
            return elementResult ? elementResult : _emptyResult;
        }
        return _emptyResult;
    }

    onChangeElementValue(evt) {
        const evaluation = this.evaluation();
        if (!evaluation?.partial) {
            return;
        }
        const _selectedElementResult = this.findElementResult(evt.id);
        if (_selectedElementResult.result !== null) {
            _selectedElementResult.result = evt.value;
        } else {
            const _elementResult = {
                id: null,
                [evt.target]: evt.id,
                evaluationPartialId: evaluation.partial.id,
                result: evt.value,
                type: evt.type
            };
            evaluation.partial.results.push(_elementResult);
        }
        this.evaluation.set({ ...evaluation });
    }

    onChangeComment(evt) {
        const evaluation = this.evaluation();
        if (!evaluation?.partial) {
            return;
        }
        const _selectedElementResult = this.findElementResult(evt.id);
        if (_selectedElementResult.comment !== null) {
            _selectedElementResult.comment = evt.comment;
        } else {
            const _elementResult = {
                id: null,
                [evt.target]: evt.id,
                evaluationPartialId: evaluation.partial.id,
                result: null,
                comment: evt.comment,
                type: evt.type
            };
            evaluation.partial.results.push(_elementResult);
        }
        this.evaluation.set({ ...evaluation });
    }

    openDialog() {
        const resultados = this.mapPoll()?.partial?.results ?? [];
        const resultadosFiltrados = resultados.filter((resultado) =>
            this.sectionsRequired.includes(resultado.elementId)
        );
        if (this.sectionsRequired.length && resultadosFiltrados.length >= this.sectionsRequired.length) {
            const dialogRef = this.dialog.open(SendPollComponent, {
                panelClass: [],
                width: "50vw",
                disableClose: false,
                autoFocus: false,
                data: {
                    confirm: this.sendPoll.bind(this),
                    close: () => dialogRef.close()
                }
            });
        } else {
            this.snackBar.open("No se ha podido enviar la encuesta. Faltan elementos requeridos", "X", {
                duration: 3000,
                verticalPosition: "bottom"
            });
        }
    }

    sendPoll() {
        let _body = this.mapPoll();
        _body.partial.status = "DONE";
        this.dialog.closeAll();
        this.evaluationSvc.savePoll(_body).subscribe((data) => {
            if (data.response === "OK") {
                this.dialog.closeAll();
                this.loadPoll();
            }
        });
    }

    mapPoll() {
        const evaluation = this.evaluation();
        let _partial = Object.assign({}, evaluation.partial);
        _partial.status = "DRAFT";
        let _evaluation = Object.assign({}, evaluation);
        _evaluation.partial = _partial;
        return _evaluation;
    }

    transform(html) {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    tooglePanel(section: any) {
        section.extended = !section.extended;
        const block = this.selectedBlock();
        if (block) {
            this.selectedBlock.set({ ...block, sections: [...block.sections] });
        }
    }
}
